import React, { useCallback, useEffect, useState } from 'react';
import { useLeague } from '@shootout-comps/core';
import { useLang } from '@shootout-comps/i18n';
import type { League, LeagueFormat, TeamStanding, WinLeaderEntry, NotableBreak } from '@shootout-comps/types';
import DivisionTabs from '../../components/DivisionTabs';

interface DivisionStandingsPageProps {
  leagueId: string;
  divisionId: string;
  onBack: () => void;
  onSwitchToTeams: () => void;
  onSwitchToFixtures: () => void;
  onSwitchToHistory: () => void;
  onOpenTeam: (teamId: string) => void;
}

const FORMAT_LABEL: Record<LeagueFormat, string> = { handicap: '🔴 Handicap', scratch: '🎯 Scratch' };

// Standings are computed on read by the API (apps/api/src/routes/league.routes.ts's
// computeStandings) — this page just fetches and renders the resulting table, re-fetching
// whenever the division's completed-fixture set could have changed.
export default function DivisionStandingsPage({ leagueId, divisionId, onBack, onSwitchToTeams, onSwitchToFixtures, onSwitchToHistory, onOpenTeam }: DivisionStandingsPageProps) {
  const { fetchLeague, fetchStandings, fetchWinLeaders, fetchBreakLeaders } = useLeague();
  const { t } = useLang();
  const [league, setLeague] = useState<League | null>(null);
  const [standings, setStandings] = useState<TeamStanding[] | null>(null);
  const [winLeaders, setWinLeaders] = useState<WinLeaderEntry[] | null>(null);
  const [breakLeaders, setBreakLeaders] = useState<NotableBreak[] | null>(null);
  const [error, setError] = useState('');

  const reload = useCallback(() => {
    fetchLeague(leagueId).then(setLeague).catch(e => setError(e instanceof Error ? e.message : 'Failed to load'));
    fetchStandings(leagueId, divisionId).then(setStandings).catch(e => setError(e instanceof Error ? e.message : 'Failed to load standings'));
    fetchWinLeaders(leagueId, divisionId).then(setWinLeaders).catch(e => setError(e instanceof Error ? e.message : 'Failed to load leaders'));
    fetchBreakLeaders(leagueId, divisionId).then(setBreakLeaders).catch(e => setError(e instanceof Error ? e.message : 'Failed to load leaders'));
  }, [fetchLeague, fetchStandings, fetchWinLeaders, fetchBreakLeaders, leagueId, divisionId]);

  useEffect(reload, [reload]);

  const division = league?.divisions.find(d => d._id === divisionId);

  if (error && !league) return <div style={{ maxWidth: '640px', margin: '2rem auto', padding: '1rem', color: '#e74c3c', textAlign: 'center' }}>{error}</div>;
  if (!league || !division || !standings) return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--chalk-dim)' }}>Loading…</div>;

  const th: React.CSSProperties = { textAlign: 'right', padding: '0.5rem 0.6rem', fontSize: '0.68rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 };
  const td: React.CSSProperties = { textAlign: 'right', padding: '0.55rem 0.6rem', fontSize: '0.85rem' };

  return (
    <div style={{ maxWidth: '760px', margin: '0 auto', padding: '2rem 1.5rem' }}>
      <button onClick={onBack} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: 'var(--chalk-dim)', padding: '0.35rem 0.75rem', fontSize: '0.8rem', cursor: 'pointer', marginBottom: '1.25rem' }}>
        ← {league.name}
      </button>

      <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.5rem,5vw,2rem)', color: 'var(--gold)', marginBottom: '0.5rem' }}>
        {division.name} <span style={{ fontSize: '0.9rem', color: 'var(--chalk-dim)', fontFamily: 'inherit' }}>{FORMAT_LABEL[division.format]}</span>
      </h2>

      <DivisionTabs active="standings" onSelect={tab => { if (tab === 'teams') onSwitchToTeams(); else if (tab === 'fixtures') onSwitchToFixtures(); else if (tab === 'history') onSwitchToHistory(); }} />

      {error && <p style={{ color: '#e74c3c', fontSize: '0.8rem', marginBottom: '1rem' }}>{error}</p>}

      {standings.length === 0 ? (
        <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', textAlign: 'center', padding: '2rem 0' }}>
          {t.noTeamsInDivision}
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <th style={{ ...th, textAlign: 'left' }}>#</th>
                <th style={{ ...th, textAlign: 'left' }}>{t.standingsTeamCol}</th>
                <th style={th}>P</th>
                <th style={th}>W</th>
                <th style={th}>D</th>
                <th style={th}>L</th>
                <th style={th}>F</th>
                <th style={th}>A</th>
                <th style={th}>+/-</th>
                <th style={{ ...th, color: 'var(--gold)' }}>Pts</th>
              </tr>
            </thead>
            <tbody>
              {standings.map((s, i) => (
                <tr
                  key={s.teamId}
                  onClick={() => onOpenTeam(s.teamId)}
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer' }}
                  className="standings-row"
                >
                  <td style={{ ...td, textAlign: 'left', color: 'var(--chalk-dim)' }}>{i + 1}</td>
                  <td style={{ ...td, textAlign: 'left', fontWeight: 600, color: 'var(--gold)' }}>{s.teamName}</td>
                  <td style={td}>{s.played}</td>
                  <td style={td}>{s.won}</td>
                  <td style={td}>{s.drawn}</td>
                  <td style={td}>{s.lost}</td>
                  <td style={td}>{s.framesFor}</td>
                  <td style={td}>{s.framesAgainst}</td>
                  <td style={{ ...td, color: s.frameDifference > 0 ? '#2ecc71' : s.frameDifference < 0 ? '#e74c3c' : 'var(--chalk-dim)' }}>
                    {s.frameDifference > 0 ? `+${s.frameDifference}` : s.frameDifference}
                  </td>
                  <td style={{ ...td, fontWeight: 700, color: 'var(--gold)' }}>{s.points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style>{`.standings-row:hover{background:rgba(255,255,255,0.03)}`}</style>

      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginTop: '2rem' }}>
        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          <h3 style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.75rem', fontWeight: 600 }}>
            🏆 Top Wins
          </h3>
          {winLeaders === null ? (
            <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem' }}>…</p>
          ) : winLeaders.length === 0 ? (
            <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem' }}>No completed matches yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {winLeaders.map((w, i) => (
                <div key={w.playerId} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', padding: '0.45rem 0.7rem' }}>
                  <span style={{ color: 'var(--chalk-dim)', fontSize: '0.75rem', width: '16px' }}>{i + 1}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.playerName}</div>
                    <div style={{ fontSize: '0.68rem', color: 'var(--chalk-dim)' }}>{w.teamName}</div>
                  </div>
                  <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.15rem', color: 'var(--gold)' }}>{w.wins}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          <h3 style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.75rem', fontWeight: 600 }}>
            🎯 Top Breaks
          </h3>
          {league.notableBreakThreshold == null ? (
            <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem' }}>No threshold configured for this league.</p>
          ) : breakLeaders === null ? (
            <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem' }}>…</p>
          ) : breakLeaders.length === 0 ? (
            <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem' }}>No breaks of {league.notableBreakThreshold}+ yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {breakLeaders.map((b, i) => (
                <div key={b._id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', padding: '0.45rem 0.7rem' }}>
                  <span style={{ color: 'var(--chalk-dim)', fontSize: '0.75rem', width: '16px' }}>{i + 1}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.playerName}</div>
                    <div style={{ fontSize: '0.68rem', color: 'var(--chalk-dim)' }}>{b.teamName}</div>
                  </div>
                  <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.15rem', color: '#2ecc71' }}>{b.points}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
